import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime } from 'rxjs';
import { AccountManagementService } from 'src/app/services/account-management.service';
import { ExportExcelService } from 'src/app/services/export-excel.service';
import { StorageService } from 'src/app/services/storage.service';
import { UserDashboardService } from 'src/app/services/user-dashboard.service';
import * as XLSX from 'xlsx';
import { DialogService } from 'primeng/dynamicdialog';
import { BulkResultComponent } from 'src/app/bulk-result/bulk-result.component';
import { TableColumn } from 'src/app/shared-components/data-table/data-table.component';

@Component({
    selector: 'app-account-listing',
    templateUrl: './account-listing.component.html',
    styleUrls: ['./account-listing.component.scss'],
    standalone: false
})
export class AccountListingComponent implements OnInit {
  readonly columns: TableColumn[] = [
    { field: 's_no', header: 'S.No' },
    { field: 'id', header: 'Id' },
    { field: 'account_name', header: 'Name' },
    { field: 'organisation_name', header: 'Organization' },
    { field: 'branch_count', header: 'No of Branches' },
    { field: 'created_on', header: 'Created On', pipe: 'date:dd MMM yyyy' },
    { field: 'status', header: 'Status' },
    { field: 'action', header: 'Action', toggleable: false },
  ];

  states: any;
  selectedState: any;
  selectedDate: any;
  accountData: any = [];
  pageNo: number = 0;
  rowsPerPage: number = 10;
  totalRecords: number = 10;
  pagenumber: number = 0;
  pageCount: number = 0;
  displayBranch!: any;
  searchText: string = '';
  onSearchTextChange: Subject<string> = new Subject<string>();
  accountExcelData: any;
  fileUrl: string;
  fileName: string;
  accountFilters: any = { search: '' };
  selectedFile: any;
  userRole: string;

  constructor(
    private router: Router,
    private messageService: MessageService,
    private exportExcelService: ExportExcelService,
    private http: HttpClient,
    private storageService: StorageService,
    private accountManagementService: AccountManagementService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef
  ) {
    this.onSearchTextChange.pipe(debounceTime(1500)).subscribe((value) => {
      this.accountFilters.search = value;
      this.getAccountDetails();
    });

    this.userRole = this.storageService.getRole() || null;
  }

  ngOnInit() {
    this.getAccountDetails();
  }

  gotoDashboard() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  export() {
    let data = {};
    this.exportExcelService.getAccountManagement(data).subscribe((res) => {
      if (res) {
        this.exportWithFormattedDates(res['content'], res['message']);
        this.messageService.add({
          severity: 'success',
          summary: 'File downloaded successfully !',
          icon: 'pi-check-circle',
        });
      }
    });
  }

  private exportWithFormattedDates(base64String: string, fileName: string) {
    const csvText = atob(base64String);
    const lines = csvText.split('\n');

    if (lines.length < 2) {
      this.exportExcelService.base64ToCSV(base64String, fileName);
      return;
    }

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
    const dateColIdx = headers.findIndex(h => h === 'created_date');

    if (dateColIdx === -1) {
      this.exportExcelService.base64ToCSV(base64String, fileName);
      return;
    }

    const updated = lines.map((line, rowIdx) => {
      if (rowIdx === 0) return line;
      const cols = line.split(',');
      if (cols[dateColIdx] !== undefined) {
        const raw = cols[dateColIdx].trim().replace(/"/g, '');
        const d = new Date(raw);
        if (!isNaN(d.getTime())) {
          const day   = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year  = d.getFullYear();
          cols[dateColIdx] = `${day}/${month}/${year}`;
        }
      }
      return cols.join(',');
    });

    const blob = new Blob([updated.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
  showSuccess() {
    this.messageService.add({
      severity: 'custom',
      summary: 'User deleted successfully!',
      icon: 'pi-check-circle',
    });
  }

  back() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  getAccountDetails() {
    this.accountManagementService
      .getAccountData(this.accountFilters)
      .subscribe((resp: any) => {
        if (resp.status == 200) {
          if (resp.content.length != 0) {
            this.accountData = resp['content'];
            this.totalRecords = resp.pagination.total_records;
            this.pagenumber = resp.pagination.previous_page_number;
            if (this.pagenumber) {
              this.pageCount =
                this.pagenumber * this.accountFilters['page_size'];
            } else {
              this.pageCount = 0;
            }
          } else {
            this.accountData = resp.content;
            this.totalRecords = 0;
          }
          this.cdr.detectChanges();
        }
      });
  }

  onPageChange(event: any) {
    this.pageNo = event.first;
    this.accountFilters['page_number'] = event.page + 1;
    this.accountFilters['page_size'] = event.rows;
    this.getAccountDetails();
  }

  viewAccount(account: any): void {
    this.router.navigate(['/doqfy/management/account-edit', account?.id]);
  }

  toggleAccountStatus(id) {
    this.accountManagementService
      .toggleAccountStatus(id)
      .subscribe((res: any) => {
        this.accountData.forEach((element) => {
          if (element.id === id) {
            element.active_status = !element.active_status;
          }
        });
      });
  }

  onChangeSearchText(value) {
    this.searchText = value.toString();
    this.onSearchTextChange.next(value);
    //this.accountFilters["searchText"] = value;
  }
  detectXssInData(data: any[]): { hasXss: boolean; maliciousRows: number[] } {
    const xssPatterns = [
      /<script/i, // script tags
      /<\/script>/i, // closing script tags
      /javascript:/i, // javascript: protocol
      /onerror=/i, // onerror event
      /onload=/i, // onload event
      /onclick=/i, // onclick event
      /<iframe/i, // iframe tags
      /<object/i, // object tags
      /<embed/i, // embed tags
      /<img[^>]*on/i, // img with events
      /eval\(/i, // eval function
      /%3cscript/i, // encoded <script
      /&lt;script/i, // HTML entity encoded
      /=/, // CSV injection - formula starts with =
      /^\+/, // CSV injection - formula starts with +
      /^-/, // CSV injection - formula starts with -
      /^@/, // CSV injection - formula starts with @
    ];

    const maliciousRows: number[] = [];
    let hasXss = false;

    data.forEach((row, index) => {
      // Check 'name' field for XSS
      if (row.name) {
        const nameValue = row.name.toString().toLowerCase().trim();
        const isMalicious = xssPatterns.some((pattern) =>
          pattern.test(nameValue)
        );

        if (isMalicious) {
          hasXss = true;
          maliciousRows.push(index + 2); // +2 because Excel rows start at 1 and header is row 1
        }
      }
    });

    return { hasXss, maliciousRows };
  }

  // Optional: Sanitize data to prevent CSV formula injection
  sanitizeExcelData(data: any[]): any[] {
    return data.map((row) => {
      const sanitizedRow = { ...row };

      // Sanitize name field
      if (sanitizedRow.name && typeof sanitizedRow.name === 'string') {
        const name = sanitizedRow.name.trim();

        // If starts with dangerous characters, prepend single quote
        if (/^[=+\-@]/.test(name)) {
          sanitizedRow.name = `'${name}`;
        }
      }

      return sanitizedRow;
    });
  }

  onFileChange(event: any) {
    const file = event.target.files[0];

    // Reset file input after processing
    const resetFileInput = () => {
      event.target.value = '';
    };

    // Validate file exists
    if (!file) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No file selected',
      });
      resetFileInput();
      return;
    }

    // Validate file type
    const validExtensions = ['.xlsx', '.xls'];
    const fileName = file.name.toLowerCase();
    const isValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!isValidExtension) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid File Type',
        detail: 'Please upload a valid Excel file (.xlsx or .xls)',
      });
      resetFileInput();
      return;
    }

    // Validate file size (e.g., max 5MB)
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSizeInBytes) {
      this.messageService.add({
        severity: 'error',
        summary: 'File Too Large',
        detail: 'File size should not exceed 5MB',
      });
      resetFileInput();
      return;
    }

    this.selectedFile = file;
    const reader = new FileReader();

    reader.onerror = () => {
      this.messageService.add({
        severity: 'error',
        summary: 'File Read Error',
        detail: 'Failed to read the file. Please try again.',
      });
      resetFileInput();
    };

    reader.onload = () => {
      try {
        const data = reader.result;
        const workBook = XLSX.read(data, { type: 'binary', cellDates: true });

        // Validate workbook has sheets
        if (!workBook.SheetNames || workBook.SheetNames.length === 0) {
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid Excel File',
            detail: 'The Excel file does not contain any sheets',
          });
          resetFileInput();
          return;
        }

        // Check if Sheet1 exists
        if (!workBook.SheetNames.includes('Sheet1')) {
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid Sheet',
            detail: 'Excel file must contain a sheet named "Sheet1"',
          });
          resetFileInput();
          return;
        }

        const jsonData: any = workBook.SheetNames.reduce(
          (initial: any, name) => {
            const sheet = workBook.Sheets[name];
            initial[name] = XLSX.utils.sheet_to_json(sheet);
            return initial;
          },
          {}
        );

        this.accountExcelData = jsonData.Sheet1;

        // Validate data exists
        if (!this.accountExcelData || this.accountExcelData.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'No Data Found',
            detail: 'No data found in excel. Please fill and upload again',
          });
          resetFileInput();
          return;
        }

        // **NEW: Validate exact columns - only 'name' and 'contact_number' allowed**
        const firstRow = this.accountExcelData[0];
        const actualColumns = Object.keys(firstRow);
        const requiredColumns = ['name', 'contact_number'];

        // Check if column count is exactly 2
        if (actualColumns.length !== 2) {
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid File Format',
            detail:
              'Excel file must contain exactly 2 columns: name and contact_number',
          });
          resetFileInput();
          return;
        }

        // Check if columns are exactly 'name' and 'contact_number'
        const hasAllRequired = requiredColumns.every((col) =>
          actualColumns.includes(col)
        );
        const hasOnlyRequired = actualColumns.every((col) =>
          requiredColumns.includes(col)
        );

        if (!hasAllRequired || !hasOnlyRequired) {
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid Columns',
            detail:
              'Excel file must contain only these columns: name and contact_number',
          });
          resetFileInput();
          return;
        }

        // Validate required columns (add your required columns here)
        const missingColumns = requiredColumns.filter(
          (col) => !(col in firstRow)
        );

        if (missingColumns.length > 0) {
          this.messageService.add({
            severity: 'error',
            summary: 'Missing Columns',
            detail: `Required columns missing: ${missingColumns.join(', ')}`,
          });
          resetFileInput();
          return;
        }

        const xssCheck = this.detectXssInData(this.accountExcelData);

        if (xssCheck.hasXss) {
          const rowNumbers = xssCheck.maliciousRows.join(', ');
          this.messageService.add({
            severity: 'error',
            summary: 'Security Alert',
            detail: `Malicious content detected in Excel file! Script tags and harmful code found in row(s): ${rowNumbers}. Please remove and try again.`,
          });
          resetFileInput();
          return;
        }

        // Process data
        this.accountExcelData.forEach((detail) => {
          if (detail.contact_number) {
            detail.contact_number = detail.contact_number.toString();
          }
        });

        this.sendData(this.accountExcelData);
        resetFileInput();
      } catch (error) {
        console.error('Error processing Excel file:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Processing Error',
          detail:
            'Failed to process the Excel file. Please ensure it is a valid Excel file.',
        });
        resetFileInput();
      }
    };

    reader.readAsBinaryString(file);
  }

  sendData(data) {
    const xssCheck = this.detectXssInData(data);

    if (xssCheck.hasXss) {
      this.messageService.add({
        severity: 'error',
        summary: 'Security Alert',
        detail: 'Malicious content detected! Data cannot be processed.',
      });
      return;
    }
    let hasInvalidContactNumber = false;

    data.forEach((element) => {
      if (!/^\d{10}$/.test(element.contact_number)) {
        this.messageService.add({
          severity: 'info',
          detail:
            'Please Add a Valid 10-digit Contact number: ' +
            element.contact_number,
        });
        hasInvalidContactNumber = true;
      }
    });

    if (hasInvalidContactNumber) {
      return; // Skip further processing if there are invalid contact numbers
    }

    const params = {
      details: data,
    };

    this.accountManagementService
      .CreateAccount(params)
      .subscribe((res: any) => {
        if (res.status === 201) {
          this.dialogService.open(BulkResultComponent, {
            header: 'Bulk Upload Results',
            width: '40%',
            style: {
              'min-height': '40vh',
              'max-height': '80vh',
              height: 'auto',
            },
            data: {
              success: res.content?.success || [{ message: res.message || 'Account created successfully' }],
              errors: res.content?.errors || [],
              entityType: 'Accounts',
            },
          });
          this.getAccountDetails();
          this.clearFile();
        }
      });
  }

  template() {
    this.fileUrl = 'assets/Sample_Files_Bulk_Upload/Account Upload.xlsx';
    this.fileName = 'bulk_account_upload.xlsx';
    this.http
      .get(this.fileUrl, { responseType: 'blob' })
      .subscribe((response: Blob) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(response);
        downloadLink.download = this.fileName;
        downloadLink.click();
      });
  }

  clearFile() {
    this.selectedFile = '';
    const inputElement = <HTMLInputElement>(
      document.getElementById('upload_file')
    );
    inputElement.value = null;
  }

  sanitizeSearchText(event: any) {
    const input = event.target as HTMLInputElement;
    // Allow only alphanumeric characters and spaces
    const sanitized = input.value.replace(/[^A-Za-z0-9 ]/g, '');

    if (input.value !== sanitized) {
      this.searchText = sanitized;
      input.value = sanitized;
    }
  }
}
